import { translate, hasTranslation } from '#layers/base/i18n/t'

const CODE_FIELDS = ['code', 'errorCode', 'reason', 'error'] as const

/**
 * Resolve a localizable error code from a response/data object. Tries several
 * fields the backend uses for codes (game uses `code`; finance/visitor use
 * `error`; verification/withdrawals use `errorCode`; auth uses `reason`).
 * A candidate counts as a code only if it's a single token (no whitespace) and
 * `errors.api.<value>` exists — so English sentences in `error`/`message` are
 * never misread as codes.
 */
function resolveApiCode(data: Record<string, unknown> | null | undefined): string | undefined {
  if (!data) return undefined
  for (const field of CODE_FIELDS) {
    const v = data[field]
    if (typeof v === 'string' && v.length > 0 && !/\s/.test(v) && hasTranslation('errors.api.' + v)) {
      return v
    }
  }
  return undefined
}

/**
 * Localize a backend error `code` (+ optional `params`) via the frontend i18n.
 * Backend codes are language-agnostic; the frontend owns the translations under
 * `errors.api.<CODE>`. Returns `fallback` when there's no code or no matching key.
 */
export function localizeErrorCode(
  code: string | undefined,
  params: Record<string, unknown> | undefined,
  fallback: string,
): string {
  if (code && code.length > 0) {
    const key = 'errors.api.' + code
    if (hasTranslation(key)) return translate(key, params ?? {})
  }
  return fallback
}

/**
 * ofetch formats its own failures as `[GET] "https://…": <no response> Failed
 * to fetch` — the whole request line, query string included. That is never copy
 * a player can act on, and QA found it rendering verbatim in the deposit form
 * (#845). A leading `[` is the marker: no backend error message starts with one.
 * (Some UI copy does — `pages.provablyFair.useLastRound` is "[ Use last round
 * values ]" — but locale strings never travel through here.)
 *
 * Applied to every text source, not just `Error.message`: a server handler that
 * catches a fetch failure and forwards `err.message` puts the same string in
 * the response body, and the in-band path used to pass it straight through.
 */
function isTransportNoise(message: string): boolean {
  return message.startsWith('[')
}

/**
 * Read an HTTP status off a thrown error. ofetch's FetchError exposes
 * `statusCode` (and `status`); some wrappers nest it under `response.status`.
 */
function readErrorStatus(error: unknown): number | undefined {
  const e = error as
    | { statusCode?: unknown; status?: unknown; response?: { status?: unknown } }
    | null
    | undefined
  const raw = e?.statusCode ?? e?.status ?? e?.response?.status
  return typeof raw === 'number' ? raw : undefined
}

// Pulls a user-friendly message out of a thrown error. Prefers a localized
// backend code (errors.api.<CODE>) resolved from code/errorCode/reason/error,
// then ofetch's data.message / data.error (English server text), then
// Error.message, then the fallback. Transport noise is skipped at every step —
// see isTransportNoise.
export function getApiErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { data?: Record<string, unknown> } | null)?.data ?? null

  const code = resolveApiCode(data)
  if (code) {
    const params = (data && typeof data.params === 'object' && data.params !== null)
      ? (data.params as Record<string, unknown>)
      : (data ?? {})
    return translate('errors.api.' + code, params)
  }

  // Server-side (5xx) failures are unexpected and never carry user-actionable,
  // localized copy — their English message ("Internal Server Error", "Something
  // went wrong…") would leak into a non-English UI. Prefer the caller's
  // localized fallback over the raw server text. (Coded 5xx are handled above.)
  const status = readErrorStatus(error)
  if (status !== undefined && status >= 500) {
    return fallback
  }

  if (
    data && typeof data.message === 'string'
    && data.message.length > 0 && !isTransportNoise(data.message)
  ) {
    return data.message
  }
  if (
    data && typeof data.error === 'string'
    && data.error.length > 0 && !isTransportNoise(data.error)
  ) {
    return data.error
  }
  if (error instanceof Error && error.message && !isTransportNoise(error.message)) {
    return error.message
  }
  return fallback
}

/**
 * Localize an in-band response object (e.g. `{ success:false, error:"CODE",
 * message:"English" }` returned in the body, not thrown). Treats the response
 * as the error `data` so the same code-resolution + fallback chain applies.
 */
export function getApiResponseMessage(response: unknown, fallback: string): string {
  return getApiErrorMessage({ data: response }, fallback)
}
